import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * Shared hook to fetch managed dropdown options from /api/app-config/dropdowns.
 * Falls back to the static `fallback` array if the API call fails or hasn't
 * resolved yet — guarantees zero regression if the backend is offline.
 *
 * Cached at module-scope so all forms share a single fetch per session.
 */
let _cache = null;
let _cachePromise = null;
const _subscribers = new Set();

export async function loadAppConfig(force = false) {
  if (_cache && !force) return _cache;
  if (_cachePromise && !force) return _cachePromise;
  _cachePromise = (async () => {
    try {
      const { data } = await api.get("/app-config/dropdowns");
      _cache = data || {};
    } catch {
      _cache = {};
    } finally {
      _cachePromise = null;
      _subscribers.forEach((fn) => {
        try { fn(_cache); } catch {}
      });
    }
    return _cache;
  })();
  return _cachePromise;
}

export function invalidateAppConfig() {
  _cache = null;
  loadAppConfig(true);
}

export function useDropdown(kind, fallback = []) {
  const [opts, setOpts] = useState(() => {
    if (_cache && _cache[kind]?.options?.length) return _cache[kind].options;
    return fallback;
  });
  useEffect(() => {
    let mounted = true;
    const apply = (cache) => {
      if (!mounted) return;
      const list = cache?.[kind]?.options;
      if (Array.isArray(list) && list.length) setOpts(list);
    };
    _subscribers.add(apply);
    loadAppConfig().then(apply);
    return () => {
      mounted = false;
      _subscribers.delete(apply);
    };
  }, [kind]);
  return opts;
}
